import React, { useState } from "react";
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import DOMPurify from "dompurify";

const normalizeRestDayContent = (content) => {
    if (!content || !String(content).trim()) return content;
    const stripped = String(content)
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (/^(REST\s*DAY\s*)+$/i.test(stripped)) return "REST DAY";
    const remainder = stripped.replace(/\bREST\s*DAY\b/gi, "").replace(/\s+/g, " ").trim();
    const sectionHeadersOnly = /^(Workout\s*Order|Warm-up|Workout\s*[1234]|Stretching)(\s+(Workout\s*Order|Warm-up|Workout\s*[1234]|Stretching))*$/i;
    if (/\bREST\s*DAY\b/i.test(stripped) && (remainder === "" || sectionHeadersOnly.test(remainder))) return "REST DAY";
    return content;
};

const parseHtmlToSections = (html) => {
    if (!html || !String(html).trim()) return [];
    const sections = [];
    const str = String(html);
    const parts = str.split(/(?=<p><strong>)/i);
    for (const part of parts) {
        const titleMatch = part.match(/<p><strong>([^<]*)<\/strong><\/p>/i);
        if (titleMatch) {
            const title = titleMatch[1].trim();
            const contentHtml = part.replace(/<p><strong>[^<]*<\/strong><\/p>/i, "").trim();
            const content = contentHtml
                .replace(/<p>/gi, "")
                .replace(/<\/p>/gi, "\n")
                .replace(/<br\s*\/?>/gi, "\n")
                .trim();
            sections.push({ title, content });
        } else if (part.trim()) {
            const content = part
                .replace(/<p>/gi, "")
                .replace(/<\/p>/gi, "\n")
                .replace(/<br\s*\/?>/gi, "\n")
                .trim();
            if (content) sections.push({ title: "", content });
        }
    }
    return sections;
};

const normalizeMarkdownInput = (value) => String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/^\s*---\s+(.+)$/gm, "- $1")
    .replace(/^\s*\$\$\s+(.+)$/gm, "- $1");

const decodeHtmlEntities = (value) => String(value || "")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2F;/gi, "/");

const escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const inlineMarkdownToHtml = (value) => String(value || "")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/~~(.+?)~~/g, "<s>$1</s>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');

const parseTwoColumnRow = (line) => {
    if (!line || (!line.includes("//") && !line.includes("|"))) return null;

    let normalizedLine = line.trim();
    if (
        (normalizedLine.startsWith("**") && normalizedLine.endsWith("**")) ||
        (normalizedLine.startsWith("__") && normalizedLine.endsWith("__"))
    ) {
        normalizedLine = normalizedLine.slice(2, -2).trim();
    }

    let delimiter = "//";
    let splitIndex = normalizedLine.indexOf("//");
    if (splitIndex === -1) {
        delimiter = "|";
        splitIndex = normalizedLine.indexOf("|");
        if (/^\|?[\s:\-]+(\|[\s:\-]+)+\|?$/.test(normalizedLine)) return null;
    }
    if (splitIndex <= 0 || splitIndex >= normalizedLine.length - delimiter.length) return null;

    const left = normalizedLine.slice(0, splitIndex).trim();
    const right = normalizedLine.slice(splitIndex + delimiter.length).trim();
    if (!left || !right) return null;

    return {
        left: inlineMarkdownToHtml(escapeHtml(left)),
        right: inlineMarkdownToHtml(escapeHtml(right))
    };
};

const markdownToHtml = (value) => {
    const normalized = normalizeMarkdownInput(value);
    if (!normalized.trim()) return "";

    const lines = normalized.split("\n");
    const html = [];
    let listType = null;
    let tableRows = [];
    let blankLineCount = 0;

    const flushList = () => {
        if (!listType) return;
        html.push(`</${listType}>`);
        listType = null;
    };
    const flushTable = () => {
        if (!tableRows.length) return;
        html.push("<table><tbody>");
        tableRows.forEach((row) => {
            html.push(`<tr><td>${row.left}</td><td>${row.right}</td></tr>`);
        });
        html.push("</tbody></table>");
        tableRows = [];
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) {
            flushList();
            flushTable();
            blankLineCount += 1;
            if (blankLineCount <= 2) {
                html.push("<p><br></p>");
            }
            continue;
        }
        blankLineCount = 0;

        const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
            flushList();
            flushTable();
            const level = headingMatch[1].length;
            html.push(`<h${level}>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(headingMatch[2])))}</h${level}>`);
            continue;
        }

        if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
            flushList();
            flushTable();
            html.push("<hr>");
            continue;
        }

        const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
        if (unorderedMatch) {
            flushTable();
            if (listType !== "ul") {
                flushList();
                listType = "ul";
                html.push("<ul>");
            }
            html.push(`<li>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(unorderedMatch[1])))}</li>`);
            continue;
        }

        const orderedMatch = line.match(/^\d+[.)]\s+(.+)$/);
        if (orderedMatch) {
            flushTable();
            if (listType !== "ol") {
                flushList();
                listType = "ol";
                html.push("<ol>");
            }
            html.push(`<li>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(orderedMatch[1])))}</li>`);
            continue;
        }

        const row = parseTwoColumnRow(line);
        if (row) {
            flushList();
            tableRows.push(row);
            continue;
        }

        flushList();
        flushTable();
        html.push(`<p>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(line)))}</p>`);
    }

    flushList();
    flushTable();
    return html.join("");
};

const toSanitizedRichHtml = (content) => {
    if (!content) return "";
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(content);
    const normalized = hasHtml ? content : markdownToHtml(content);
    return DOMPurify.sanitize(normalized, {
        ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "span", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "a", "code", "s", "table", "tbody", "tr", "td"],
        ALLOWED_ATTR: ["style", "href", "target", "rel"],
        ALLOWED_STYLES: {
            "*": {
                color: [
                    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
                    /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/
                ]
            }
        }
    });
};

const richContentSx = {
    color: "text.primary",
    fontSize: "0.875rem",
    lineHeight: 1.65,
    "& p": { margin: 0 },
    "& p + p": { mt: 0.5 },
    "& ul, & ol": { pl: 2, my: 0.5 },
    "& h1": { fontSize: "1.5rem", fontWeight: 700, mt: 1.5, mb: 0.75, lineHeight: 1.3 },
    "& h2": { fontSize: "1.25rem", fontWeight: 700, mt: 1.25, mb: 0.6, lineHeight: 1.3 },
    "& h3": { fontSize: "1.1rem", fontWeight: 700, mt: 1, mb: 0.5, lineHeight: 1.35 },
    "& h4": { fontSize: "1rem", fontWeight: 600, mt: 0.75, mb: 0.4, lineHeight: 1.4 },
    "& h5": { fontSize: "0.95rem", fontWeight: 600, mt: 0.6, mb: 0.35, lineHeight: 1.4 },
    "& h6": { fontSize: "0.9rem", fontWeight: 600, mt: 0.5, mb: 0.3, lineHeight: 1.45 },
    "& code": { bgcolor: "#eeeeee", px: 0.5, borderRadius: 0.5, fontFamily: "monospace" },
    "& table": { width: "100%", borderCollapse: "collapse", mt: 0.5, mb: 0.5 },
    "& td": { p: "6px 10px", border: "none", verticalAlign: "top" },
    "& tr:nth-of-type(odd)": { bgcolor: "rgba(0, 0, 0, 0.06)" },
    "& tr:nth-of-type(even)": { bgcolor: "transparent" },
    "& tr:first-of-type td": { fontWeight: 700 }
};

export default function CompeteInfoModal({
    open,
    onClose,
    content,
    title = "Compete",
    emptyMessage = "No compete info for this class.",
    normalizeRestDay = true,
    dialogProps = {},
    onAddToTrainings,
    showAddToTrainings = false
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [isAdding, setIsAdding] = useState(false);
    const normalized = normalizeRestDay ? normalizeRestDayContent(content || "") : (content || "");
    const sections = parseHtmlToSections(toSanitizedRichHtml(normalized));

    const handleAddToTrainings = async () => {
        if (!onAddToTrainings || isAdding) return;
        setIsAdding(true);
        try {
            await onAddToTrainings();
            onClose?.();
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
            {...dialogProps}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                {content && String(content).trim() ? (
                    sections.length > 0 ? (
                        <Stack spacing={2}>
                            {sections.map((section, index) => (
                                <Card key={`compete-${index}`} sx={{ borderRadius: 2, boxShadow: 1 }}>
                                    <CardContent>
                                        {section.title ? (
                                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                                {section.title}
                                            </Typography>
                                        ) : null}
                                        <Box dangerouslySetInnerHTML={{ __html: toSanitizedRichHtml(section.content) }} sx={richContentSx} />
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    ) : (
                        <Box dangerouslySetInnerHTML={{ __html: toSanitizedRichHtml(normalized) }} sx={richContentSx} />
                    )
                ) : (
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between" }}>
                <Box>
                    {showAddToTrainings && onAddToTrainings && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={isAdding ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                            onClick={handleAddToTrainings}
                            disabled={isAdding}
                            sx={{ mr: 1 }}
                        >
                            {isAdding ? "Adding..." : "Add to trainings"}
                        </Button>
                    )}
                </Box>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
